import { Injectable } from "@angular/core";

export interface LocationCoordinates {
  lat: number;
  lng: number;
}

export interface LocationInfo {
  city?: string;
  country?: string;
  region?: string;
  countries?: string[];
  displayName?: string;
  coordinates?: LocationCoordinates;
}

@Injectable({
  providedIn: "root",
})
export class LocationIntelligenceService {
  // Known countries for case-insensitive matching
  private knownCountries = new Set([
    // Tier 1
    "united states of america",
    "united states",
    // Tier 2
    "france",
    "india",
    "united kingdom",
    "canada",
    // Tier 3
    "germany",
    "poland",
    "australia",
    "philippines",
    "spain",
    "brazil",
    "mexico",
    "portugal",
    "israel",
    "netherlands",
    "china",
    "bulgaria",
    // Tier 4
    "colombia",
    "pakistan",
    "singapore",
    "egypt",
    "lithuania",
    "italy",
    "ireland",
    "united arab emirates",
    "uae",
    "greece",
    "ukraine",
    "sweden",
    "switzerland",
    "argentina",
    "japan",
    "vietnam",
    "indonesia",
    "morocco",
    "czechia",
    "belgium",
    "new zealand",
    "peru",
    "south korea",
    // Additional common countries
    "turkey",
    "austria",
    "denmark",
    "norway",
    "finland",
    "romania",
    "hungary",
    "croatia",
    "serbia",
    "slovakia",
    "slovenia",
    "estonia",
    "latvia",
    "chile",
    "south africa",
    "kenya",
    "nigeria",
    "malaysia",
    "thailand",
    "taiwan",
    "hong kong",
    "saudi arabia",
    "costa rica",
    "uruguay",
    "cameroon",
    "dominican republic",
    "malta",
    "lebanon",
    "nicaragua",
    "north macedonia",
    "qatar",
    "bahrain",
    "kosovo",
    "nepal",
    "rwanda",
    "jordan",
    "sri lanka",
  ]);

  // Region/continent to countries mapping for geographic filtering
  private regionCountries: { [key: string]: string[] } = {
    "europe": [
      // Western Europe
      "United Kingdom", "France", "Germany", "Netherlands", "Belgium",
      "Luxembourg", "Ireland", "Switzerland", "Austria", "Liechtenstein", "Monaco",
      // Southern Europe
      "Spain", "Portugal", "Italy", "Greece", "Malta", "Cyprus",
      "Andorra", "San Marino", "Vatican City", "Gibraltar",
      // Northern Europe (Nordics + Baltics)
      "Sweden", "Norway", "Denmark", "Finland", "Iceland",
      "Estonia", "Latvia", "Lithuania",
      // Central Europe
      "Poland", "Czechia", "Czech Republic", "Hungary", "Slovakia", "Slovenia",
      // Eastern Europe / Balkans
      "Romania", "Bulgaria", "Croatia", "Serbia", "Bosnia and Herzegovina",
      "Montenegro", "North Macedonia", "Albania", "Kosovo", "Moldova",
      "Ukraine", "Belarus"
    ],
    "north america": [
      "United States of America", "Canada", "Mexico"
    ],
    "asia": [
      "India", "China", "Japan", "South Korea", "Singapore",
      "Philippines", "Vietnam", "Indonesia", "Thailand", "Malaysia",
      "Taiwan", "Hong Kong", "Pakistan", "Bangladesh", "Sri Lanka",
      "Nepal", "Israel", "United Arab Emirates", "Saudi Arabia",
      "Qatar", "Bahrain", "Jordan", "Lebanon", "Turkey"
    ],
    "south america": [
      "Brazil", "Argentina", "Colombia", "Chile", "Peru",
      "Uruguay", "Ecuador", "Venezuela", "Bolivia", "Paraguay"
    ],
    "africa": [
      "South Africa", "Egypt", "Nigeria", "Kenya", "Morocco",
      "Ghana", "Rwanda", "Ethiopia", "Tanzania", "Uganda", "Cameroon"
    ],
    "oceania": [
      "Australia", "New Zealand"
    ],
    "latin america": [
      "Mexico", "Brazil", "Argentina", "Colombia", "Chile", "Peru",
      "Uruguay", "Ecuador", "Venezuela", "Bolivia", "Paraguay",
      "Costa Rica", "Panama", "Guatemala", "Honduras", "El Salvador",
      "Nicaragua", "Dominican Republic", "Cuba", "Puerto Rico"
    ],
    "emea": [
      // Europe (all)
      "United Kingdom", "France", "Germany", "Netherlands", "Belgium",
      "Luxembourg", "Ireland", "Switzerland", "Austria",
      "Spain", "Portugal", "Italy", "Greece", "Malta", "Cyprus",
      "Sweden", "Norway", "Denmark", "Finland", "Iceland",
      "Estonia", "Latvia", "Lithuania",
      "Poland", "Czechia", "Czech Republic", "Hungary", "Slovakia", "Slovenia",
      "Romania", "Bulgaria", "Croatia", "Serbia", "Bosnia and Herzegovina",
      "Montenegro", "North Macedonia", "Albania", "Kosovo", "Moldova",
      "Ukraine", "Belarus",
      // Middle East
      "United Arab Emirates", "Saudi Arabia", "Israel", "Turkey", "Qatar", "Bahrain", "Jordan", "Lebanon",
      // Africa
      "South Africa", "Egypt", "Nigeria", "Kenya", "Morocco", "Ghana", "Rwanda", "Ethiopia"
    ],
    "apac": [
      "India", "China", "Japan", "South Korea", "Singapore",
      "Philippines", "Vietnam", "Indonesia", "Thailand", "Malaysia",
      "Taiwan", "Hong Kong", "Australia", "New Zealand", "Pakistan"
    ]
  };

  // Predefined coordinates for major cities to avoid API calls
  private cityCoordinates: { [key: string]: LocationCoordinates } = {
    // Portugal
    "Porto, Portugal": { lat: 41.1579, lng: -8.6291 },
    "Coimbra, Portugal": { lat: 40.2033, lng: -8.4103 },
    "Braga, Portugal": { lat: 41.5454, lng: -8.4265 },
    "Aveiro, Portugal": { lat: 40.6413, lng: -8.6535 },

    // United Kingdom
    "London, United Kingdom": { lat: 51.5074, lng: -0.1278 },
    "Manchester, United Kingdom": { lat: 53.4808, lng: -2.2426 },
    "Birmingham, United Kingdom": { lat: 52.4862, lng: -1.8904 },
    "Edinburgh, United Kingdom": { lat: 55.9533, lng: -3.1883 },
    "Glasgow, United Kingdom": { lat: 55.8642, lng: -4.2518 },
    "Cardiff, United Kingdom": { lat: 51.4816, lng: -3.1791 },

    // Spain
    "Madrid, Spain": { lat: 40.4168, lng: -3.7038 },
    "Barcelona, Spain": { lat: 41.3851, lng: 2.1734 },
    "Valencia, Spain": { lat: 39.4699, lng: -0.3763 },
    "Seville, Spain": { lat: 37.3886, lng: -5.9823 },
    "Bilbao, Spain": { lat: 43.2627, lng: -2.9253 },

    // Germany - Major tech hubs
    "Berlin, Germany": { lat: 52.52, lng: 13.405 }, // 25 jobs
    "Hamburg, Germany": { lat: 53.5511, lng: 9.9937 }, // 14 jobs
    "Munich, Germany": { lat: 48.1351, lng: 11.582 }, // 10 jobs
    "Frankfurt, Germany": { lat: 50.1109, lng: 8.6821 },
    "Cologne, Germany": { lat: 50.9375, lng: 6.9603 },

    // Netherlands
    "Amsterdam, Netherlands": { lat: 52.3676, lng: 4.9041 }, // 13 jobs
    "Rotterdam, Netherlands": { lat: 51.9244, lng: 4.4777 },
    "The Hague, Netherlands": { lat: 52.0705, lng: 4.3007 },
    "Utrecht, Netherlands": { lat: 52.0907, lng: 5.1214 },

    // France - Top cities by job volume
    "Paris, France": { lat: 48.8566, lng: 2.3522 }, // 140 jobs
    "Lyon, France": { lat: 45.764, lng: 4.8357 }, // 11 jobs
    "Lille, France": { lat: 50.6292, lng: 3.0573 }, // 10 jobs
    "Marseille, France": { lat: 43.2965, lng: 5.3698 },
    "Toulouse, France": { lat: 43.6047, lng: 1.4442 },
    "Nice, France": { lat: 43.7102, lng: 7.262 },

    // United States (using standardized database format) - Top US cities by job volume
    "New York, United States of America": { lat: 40.7128, lng: -74.006 }, // 236 jobs
    "San Francisco, United States of America": { lat: 37.7749, lng: -122.4194 }, // 171 jobs
    "Seattle, United States of America": { lat: 47.6062, lng: -122.3321 }, // 55 jobs
    "Austin, United States of America": { lat: 30.2672, lng: -97.7431 }, // 51 jobs
    "Boston, United States of America": { lat: 42.3601, lng: -71.0589 }, // 45 jobs
    "Los Angeles, United States of America": { lat: 34.0522, lng: -118.2437 }, // 37 jobs
    "Chicago, United States of America": { lat: 41.8781, lng: -87.6298 }, // 35 jobs
    "Atlanta, United States of America": { lat: 33.749, lng: -84.388 }, // 31 jobs
    "Mountain View, United States of America": { lat: 37.3861, lng: -122.0839 }, // 24 jobs
    "Washington, United States of America": { lat: 38.9072, lng: -77.0369 }, // 23 jobs
    "San Jose, United States of America": { lat: 37.3382, lng: -121.8863 }, // 23 jobs
    "Denver, United States of America": { lat: 39.7392, lng: -104.9903 }, // 18 jobs
    // Also include short form for backward compatibility
    "New York, United States": { lat: 40.7128, lng: -74.006 },
    "Los Angeles, United States": { lat: 34.0522, lng: -118.2437 },
    "Chicago, United States": { lat: 41.8781, lng: -87.6298 },
    "San Francisco, United States": { lat: 37.7749, lng: -122.4194 },
    "Seattle, United States": { lat: 47.6062, lng: -122.3321 },
    "Boston, United States": { lat: 42.3601, lng: -71.0589 },
    "Austin, United States": { lat: 30.2672, lng: -97.7431 },

    // Italy
    "Rome, Italy": { lat: 41.9028, lng: 12.4964 },
    "Milan, Italy": { lat: 45.4642, lng: 9.19 },
    "Naples, Italy": { lat: 40.8518, lng: 14.2681 },
    "Florence, Italy": { lat: 43.7696, lng: 11.2558 },

    // India - Major tech hubs
    "Bengaluru, India": { lat: 12.9716, lng: 77.5946 }, // 63 jobs
    "Hyderabad, India": { lat: 17.385, lng: 78.4867 }, // 27 jobs
    "Gurugram, India": { lat: 28.4595, lng: 77.0266 }, // 11 jobs

    // Canada - Major cities
    "Toronto, Canada": { lat: 43.6532, lng: -79.3832 }, // 42 jobs
    "Vancouver, Canada": { lat: 49.2827, lng: -123.1207 }, // 13 jobs
    "Montreal, Canada": { lat: 45.5017, lng: -73.5673 }, // 10 jobs

    // Other high-volume international cities
    "Sydney, Australia": { lat: -33.8688, lng: 151.2093 }, // 24 jobs
    "Warsaw, Poland": { lat: 52.2297, lng: 21.0122 }, // 22 jobs
    "Sofia, Bulgaria": { lat: 42.6977, lng: 23.3219 }, // 16 jobs
    "Lisbon, Portugal": { lat: 38.7223, lng: -9.1393 }, // 13 jobs
    "Athens, Greece": { lat: 37.9838, lng: 23.7275 }, // 12 jobs
    "Leeds, United Kingdom": { lat: 53.8008, lng: -1.5491 }, // 12 jobs
    "Tel-Aviv, Israel": { lat: 32.0853, lng: 34.7818 }, // 10 jobs
    "Cairo, Egypt": { lat: 30.0444, lng: 31.2357 }, // 10 jobs
    "Ho Chi Minh City, Vietnam": { lat: 10.8231, lng: 106.6297 }, // 9 jobs

    // Other European cities
    "Brussels, Belgium": { lat: 50.8503, lng: 4.3517 },
    "Zurich, Switzerland": { lat: 47.3769, lng: 8.5417 },
    "Vienna, Austria": { lat: 48.2082, lng: 16.3738 },
    "Stockholm, Sweden": { lat: 59.3293, lng: 18.0686 },
    "Copenhagen, Denmark": { lat: 55.6761, lng: 12.5683 },
    "Oslo, Norway": { lat: 59.9139, lng: 10.7522 },
    "Helsinki, Finland": { lat: 60.1699, lng: 24.9384 },
  };

  constructor() {}

  /**
   * Parse location query string and return detailed location information
   */
  parseLocationQuery(locationQuery: string): LocationInfo {
    if (!locationQuery || locationQuery.trim() === "") {
      return {};
    }

    const cleanQuery = locationQuery.trim();

    // Handle URL-formatted locations from sitemap (e.g., "new-york-united-states")
    if (cleanQuery.includes("-") && !cleanQuery.includes(",")) {
      const parts = cleanQuery.split("-");
      if (parts.length >= 2) {
        // Reverse the URL normalization for known countries first
        // Prioritized based on ACTUAL job volume from database analysis
        const countryReverseMappings: { [key: string]: string } = {
          // Tier 1: United States (1,689 jobs - 60% of all jobs)
          "united-states-of-america": "United States of America",
          "united-states": "United States of America",

          // Tier 2: High-volume markets (100+ jobs)
          france: "France", // 242 jobs
          india: "India", // 178 jobs
          "united-kingdom": "United Kingdom", // 142 jobs
          canada: "Canada", // 117 jobs

          // Tier 3: Medium-volume markets (20+ jobs)
          germany: "Germany", // 71 jobs
          poland: "Poland", // 40 jobs
          australia: "Australia", // 38 jobs
          philippines: "Philippines", // 37 jobs
          spain: "Spain", // 28 jobs
          brazil: "Brazil", // 27 jobs
          mexico: "Mexico", // 25 jobs
          portugal: "Portugal", // 24 jobs
          israel: "Israel", // 24 jobs
          netherlands: "Netherlands", // 24 jobs
          china: "China", // 22 jobs
          bulgaria: "Bulgaria", // 20 jobs

          // Tier 4: Lower-volume but established markets (10+ jobs)
          colombia: "Colombia",
          pakistan: "Pakistan",
          singapore: "Singapore",
          egypt: "Egypt",
          lithuania: "Lithuania",
          italy: "Italy",
          ireland: "Ireland",
          "united-arab-emirates": "United Arab Emirates",
          greece: "Greece",
          ukraine: "Ukraine",
          sweden: "Sweden",
          switzerland: "Switzerland",
          argentina: "Argentina",
          japan: "Japan",
          vietnam: "Vietnam",
          indonesia: "Indonesia",
          morocco: "Morocco",
          czechia: "Czechia",
          belgium: "Belgium",
          "new-zealand": "New Zealand",
          peru: "Peru",

          // Additional mappings for common variations
          "south-korea": "South Korea",
          "south-africa": "South Africa",
          "saudi-arabia": "Saudi Arabia",
          "costa-rica": "Costa Rica",
          "dominican-republic": "Dominican Republic",
          uruguay: "Uruguay",
          cameroon: "Cameroon",
          austria: "Austria",
          denmark: "Denmark",
          norway: "Norway",
          finland: "Finland",
          romania: "Romania",
          hungary: "Hungary",
          croatia: "Croatia",
          serbia: "Serbia",
          slovakia: "Slovakia",
          slovenia: "Slovenia",
          estonia: "Estonia",
          latvia: "Latvia",
          chile: "Chile",
          kenya: "Kenya",
          nigeria: "Nigeria",
          malaysia: "Malaysia",
          thailand: "Thailand",
          taiwan: "Taiwan",
          turkey: "Turkey",
          malta: "Malta",
          lebanon: "Lebanon",
          nicaragua: "Nicaragua",
          "north-macedonia": "North Macedonia",
          qatar: "Qatar",
          bahrain: "Bahrain",
          kosovo: "Kosovo",
          nepal: "Nepal",
          rwanda: "Rwanda",
          jordan: "Jordan",
          "sri-lanka": "Sri Lanka",
          uae: "United Arab Emirates",
        };

        // Find the country part by checking known country patterns
        let cityParts: string[] = [];
        let countryPart = "";

        // Check for known country patterns from the end (case-insensitive)
        for (let i = parts.length; i >= 1; i--) {
          const potentialCountry = parts.slice(i - 1).join("-").toLowerCase();
          if (countryReverseMappings[potentialCountry]) {
            cityParts = parts.slice(0, i - 1);
            countryPart = potentialCountry;
            break;
          }
        }

        // If no known country found, assume last part is country
        if (!countryPart) {
          cityParts = parts.slice(0, -1);
          countryPart = parts[parts.length - 1];
        }

        const city = this.capitalizeWords(cityParts.join(" "));
        const country =
          countryReverseMappings[countryPart] ||
          this.capitalizeWords(countryPart);
        // Only add comma if there's a city
        const displayName = city ? `${city}, ${country}` : country;

        return {
          city: city || undefined,
          country,
          displayName,
          coordinates: this.getCoordinatesForLocation(displayName),
        };
      }
    }

    // Handle "city, country" or "city, state, country" format
    if (cleanQuery.includes(",")) {
      const parts = cleanQuery.split(",").map((part) => part.trim());
      if (parts.length >= 2) {
        const city = this.capitalizeWords(parts[0]);
        // If 3 parts (City, State, Country), use the last part as country
        const country = this.capitalizeWords(parts[parts.length - 1]);
        const displayName = `${city}, ${country}`;

        return {
          city,
          country,
          displayName,
          coordinates: this.getCoordinatesForLocation(displayName),
        };
      }
    }

    // Handle single location (could be city or country)
    const capitalizedQuery = this.capitalizeWords(cleanQuery);

    // Check if it's a known city with coordinates
    const cityMatch = Object.keys(this.cityCoordinates).find((key) =>
      key.toLowerCase().startsWith(capitalizedQuery.toLowerCase())
    );

    if (cityMatch) {
      const parts = cityMatch.split(", ");
      return {
        city: parts[0],
        country: parts[1],
        displayName: cityMatch,
        coordinates: this.cityCoordinates[cityMatch],
      };
    }

    // Check if it's a known country (case-insensitive)
    const lowerQuery = cleanQuery.toLowerCase();
    if (this.knownCountries.has(lowerQuery)) {
      // It's a known country
      return {
        country: capitalizedQuery,
        displayName: capitalizedQuery,
        coordinates: this.getCoordinatesForLocation(capitalizedQuery),
      };
    }

    // Check if it's a known region/continent (e.g., "Europe", "Asia", "EMEA")
    if (this.regionCountries[lowerQuery]) {
      return {
        region: capitalizedQuery,
        countries: this.regionCountries[lowerQuery],
        displayName: capitalizedQuery,
      };
    }

    // Heuristic: If the location is a single word or capitalized phrase without special patterns,
    // it's more likely a country than a city. Cities are usually formatted with commas.
    // Examples: "India", "France", "United Kingdom" -> countries
    //           "london", "new york" -> cities (but would fail city match above)
    
    // Check for common city indicators (lowercase, multiple words without proper capitalization)
    const looksLikeCity = 
      cleanQuery.toLowerCase() === cleanQuery && // all lowercase
      !cleanQuery.includes(" of ") && // not "United States of America"
      cleanQuery.split(" ").length <= 2; // short phrase

    if (looksLikeCity) {
      // Likely a city name typed in lowercase
      return {
        city: capitalizedQuery,
        displayName: capitalizedQuery,
        coordinates: this.getCoordinatesForLocation(capitalizedQuery),
      };
    }

    // Default: Assume it's a country
    // This handles: "India", "Croatia", "Hungary", "United Kingdom", etc.
    return {
      country: capitalizedQuery,
      displayName: capitalizedQuery,
      coordinates: this.getCoordinatesForLocation(capitalizedQuery),
    };
  }

  /**
   * Get coordinates for a location
   */
  private getCoordinatesForLocation(
    location: string
  ): LocationCoordinates | undefined {
    // First try exact match
    const exactMatch = this.cityCoordinates[location];
    if (exactMatch) {
      return exactMatch;
    }

    // Try case-insensitive match
    const lowerLocation = location.toLowerCase();
    const matchedKey = Object.keys(this.cityCoordinates).find(
      (key) => key.toLowerCase() === lowerLocation
    );

    if (matchedKey) {
      return this.cityCoordinates[matchedKey];
    }

    // Try partial match for city names
    const partialMatch = Object.keys(this.cityCoordinates).find(
      (key) =>
        key.toLowerCase().includes(lowerLocation) ||
        lowerLocation.includes(key.toLowerCase())
    );

    if (partialMatch) {
      return this.cityCoordinates[partialMatch];
    }

    return undefined;
  }

  /**
   * Capitalize each word in a string, preserving lowercase articles/prepositions
   */
  private capitalizeWords(str: string): string {
    // Words that should remain lowercase (articles, prepositions, conjunctions)
    const lowercaseWords = new Set(['of', 'the', 'and', 'de', 'da', 'del', 'la', 'le', 'des']);
    
    return str
      .split(" ")
      .map((word, index) => {
        const lowerWord = word.toLowerCase();
        // Keep certain words lowercase unless they're the first word
        if (index > 0 && lowercaseWords.has(lowerWord)) {
          return lowerWord;
        }
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(" ");
  }

  /**
   * Get popular job locations
   */
  getPopularLocations(): string[] {
    return [
      "London, United Kingdom",
      "Berlin, Germany",
      "Amsterdam, Netherlands",
      "Paris, France",
      "Barcelona, Spain",
      "Lisbon, Portugal",
      "Madrid, Spain",
      "Milan, Italy",
      "Stockholm, Sweden",
      "Copenhagen, Denmark",
      "New York, United States of America",
      "San Francisco, United States of America",
      "Los Angeles, United States of America",
      "Remote",
    ];
  }

  /**
   * Get location-specific insights
   */
  getLocationInsights(location: string): {
    timezone?: string;
    currency?: string;
    language?: string;
  } {
    const locationInfo = this.parseLocationQuery(location);

    if (!locationInfo.country) {
      return {};
    }

    const insights: { [key: string]: any } = {
      Portugal: {
        timezone: "Europe/Lisbon",
        currency: "EUR",
        language: "Portuguese",
      },
      Spain: {
        timezone: "Europe/Madrid",
        currency: "EUR",
        language: "Spanish",
      },
      France: { timezone: "Europe/Paris", currency: "EUR", language: "French" },
      Germany: {
        timezone: "Europe/Berlin",
        currency: "EUR",
        language: "German",
      },
      Netherlands: {
        timezone: "Europe/Amsterdam",
        currency: "EUR",
        language: "Dutch",
      },
      "United Kingdom": {
        timezone: "Europe/London",
        currency: "GBP",
        language: "English",
      },
      "United States": {
        timezone: "America/New_York",
        currency: "USD",
        language: "English",
      },
      Italy: { timezone: "Europe/Rome", currency: "EUR", language: "Italian" },
      Belgium: {
        timezone: "Europe/Brussels",
        currency: "EUR",
        language: "Dutch",
      },
      Switzerland: {
        timezone: "Europe/Zurich",
        currency: "CHF",
        language: "German",
      },
      Austria: {
        timezone: "Europe/Vienna",
        currency: "EUR",
        language: "German",
      },
    };

    return insights[locationInfo.country] || {};
  }

  /**
   * Format location for SEO (used in URLs and meta tags)
   */
  formatLocationForSEO(location: string): string {
    const locationInfo = this.parseLocationQuery(location);

    if (locationInfo.city && locationInfo.country) {
      // Normalize country names for cleaner URLs
      const normalizedCountry = this.normalizeCountryForURL(
        locationInfo.country
      );
      return `${locationInfo.city
        .toLowerCase()
        .replace(/\s+/g, "-")}-${normalizedCountry}`;
    }

    return location.toLowerCase().replace(/\s+/g, "-").replace(/,/g, "");
  }

  /**
   * Normalize country names for cleaner, shorter URLs
   */
  private normalizeCountryForURL(country: string): string {
    const countryMappings: { [key: string]: string } = {
      // Tier 1: United States (1,689 jobs - 60% of all jobs)
      "united states of america": "united-states",
      "united states": "united-states",

      // Tier 2: High-volume markets (100+ jobs)
      france: "france", // 242 jobs
      india: "india", // 178 jobs
      "united kingdom": "united-kingdom", // 142 jobs
      canada: "canada", // 117 jobs

      // Tier 3: Medium-volume markets (20+ jobs)
      germany: "germany", // 71 jobs
      poland: "poland", // 40 jobs
      australia: "australia", // 38 jobs
      philippines: "philippines", // 37 jobs
      spain: "spain", // 28 jobs
      brazil: "brazil", // 27 jobs
      mexico: "mexico", // 25 jobs
      portugal: "portugal", // 24 jobs
      israel: "israel", // 24 jobs
      netherlands: "netherlands", // 24 jobs
      china: "china", // 22 jobs
      bulgaria: "bulgaria", // 20 jobs

      // Tier 4: Lower-volume but established markets (10+ jobs)
      colombia: "colombia",
      pakistan: "pakistan",
      singapore: "singapore",
      egypt: "egypt",
      lithuania: "lithuania",
      italy: "italy",
      ireland: "ireland",
      "united arab emirates": "united-arab-emirates",
      greece: "greece",
      ukraine: "ukraine",
      sweden: "sweden",
      switzerland: "switzerland",
      argentina: "argentina",
      japan: "japan",
      vietnam: "vietnam",
      indonesia: "indonesia",
      morocco: "morocco",
      czechia: "czechia",
      belgium: "belgium",
      "new zealand": "new-zealand",
      peru: "peru",

      // Additional mappings for common variations
      "south korea": "south-korea",
    };

    const normalizedInput = country.toLowerCase();
    return (
      countryMappings[normalizedInput] ||
      country.toLowerCase().replace(/\s+/g, "-")
    );
  }

  /**
   * Get region code for a location (used in geo meta tags)
   */
  getRegionCode(location: string): string | null {
    const locationInfo = this.parseLocationQuery(location);

    if (!locationInfo.city || !locationInfo.country) {
      return null;
    }

    const regionMappings: { [key: string]: { [key: string]: string } } = {
      Portugal: {
        Lisbon: "PT-11",
        Porto: "PT-13",
        Coimbra: "PT-06",
        Braga: "PT-03",
        Aveiro: "PT-01",
      },
      "United Kingdom": {
        London: "GB-ENG",
        Manchester: "GB-ENG",
        Birmingham: "GB-ENG",
        Edinburgh: "GB-SCT",
        Glasgow: "GB-SCT",
        Cardiff: "GB-WLS",
      },
      Spain: {
        Madrid: "ES-MD",
        Barcelona: "ES-CT",
        Valencia: "ES-VC",
        Seville: "ES-AN",
        Bilbao: "ES-PV",
      },
      Germany: {
        Berlin: "DE-BE",
        Munich: "DE-BY",
        Hamburg: "DE-HH",
        Frankfurt: "DE-HE",
        Cologne: "DE-NW",
      },
      Netherlands: {
        Amsterdam: "NL-NH",
        Rotterdam: "NL-ZH",
        "The Hague": "NL-ZH",
        Utrecht: "NL-UT",
      },
      France: {
        Paris: "FR-75",
        Lyon: "FR-69",
        Marseille: "FR-13",
        Toulouse: "FR-31",
        Nice: "FR-06",
      },
      "United States": {
        "New York": "US-NY",
        "Los Angeles": "US-CA",
        Chicago: "US-IL",
        "San Francisco": "US-CA",
        Seattle: "US-WA",
        Boston: "US-MA",
        Austin: "US-TX",
      },
    };

    return regionMappings[locationInfo.country]?.[locationInfo.city] || null;
  }
}
